import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  computeSupplierState,
  computeComplianceCounts,
  groupDocumentsByState,
  stripeContent,
  todayIso,
  SUPPLIER_STATES,
} from '../lib/compliance';
import { signatureStandingFor, resignRecordPatch } from '../lib/documentVersion';
import { isApprovedAcknowledgement } from '../lib/complianceRecord';

// Single source of truth for compliance state across the supplier portal.
// Used by:
//   - SupplierPortal dashboard (stripe + briefs blocked empty state)
//   - SupplierCompliance page (stripe + status card + grouped doc rows)
// Both surfaces stay in lockstep because they share this hook — a
// document update on one re-renders the other automatically on next
// focus / refetch.
//
// Returns the supplier-level state, the doc-level groupings, the counts
// for the status card, the master required-docs list, and a refetch
// function to call after a successful upload. Inputs: the supplierId
// (looked up by the caller from supplier_users) and the supplier's
// risk_flag (already on the supplier row).
export function useSupplierCompliance({ supplierId, riskFlag }) {
  const [requiredDocs, setRequiredDocs] = useState([]); // master meta from fusion_documents
  const [records, setRecords] = useState([]);           // per-supplier rows
  const [acknowledgements, setAcknowledgements] = useState([]); // per-supplier ack rows
  const [clarificationMessages, setClarificationMessages] = useState([]); // reject/clarify thread
  const [requiredKeys, setRequiredKeys] = useState([]); // keys gated by risk_flag + per-supplier overrides
  const [submissions, setSubmissions] = useState([]);   // hosted-form / pdf_sign signatures, newest first
  const [versionsByKey, setVersionsByKey] = useState({}); // published versions per document key
  const [overrideStatus, setOverrideStatus] = useState(null); // suppliers.compliance_status_override
  const [loading, setLoading] = useState(true);

  const loadMasterDocs = useCallback(async () => {
    const { data } = await supabase
      .from('fusion_documents')
      .select('id, version_value, compliance_doc_key, document_name, description, accepted_formats, max_size_mb, file_url, satisfied_by, lifecycle, source, content_richtext, content_file_url, content_file_name, change_summary, form_schema, source_pdf_path, field_layout, content_hash, supplier_provides_expiry')
      .not('compliance_doc_key', 'is', null)
      .eq('is_active', true)
      // Exclude DRAFTS: required docs are created is_active=true so they show in
      // the Risk Flag matrix, but a draft must not become a live requirement for
      // suppliers until it's published. Legacy rows have a null status (they
      // predate the draft/active field), so keep those.
      .or('status.is.null,status.neq.draft')
      .order('document_name');

    return (data || []).map(d => ({
      documentId: d.id,
      versionValue: d.version_value,
      // The hash taken when this version was published. Re-derived at signing
      // and compared, so content that changed after publish can't be signed
      // (document-versioning §3).
      contentHash: d.content_hash || null,
      key: d.compliance_doc_key,
      label: d.document_name,
      description: d.description || null,
      acceptedFormats: d.accepted_formats || null,
      maxSizeMb: d.max_size_mb || null,
      // "Supplier must provide expiry date on upload". The supplier side never
      // read this, so the upload modal asked for an expiry — and REQUIRED it —
      // on every document regardless of the admin setting. Only true counts as
      // on: when it's off (or unset) the admin sets the expiry at review, which
      // is what the toggle's own help text says.
      supplierProvidesExpiry: d.supplier_provides_expiry === true,
      templateUrl: d.file_url || null,
      // Acknowledge-type config — null on upload-type docs
      satisfiedBy: d.satisfied_by || 'upload',
      formSchema: d.form_schema || null,
      // pdf_sign — the source PDF + the placed fields
      sourcePdfPath: d.source_pdf_path || null,
      fieldLayout: Array.isArray(d.field_layout) ? d.field_layout : null,
      lifecycle: d.lifecycle || null,
      source: d.source || null,
      contentRichtext: d.content_richtext || null,
      contentFileUrl: d.content_file_url || null,
      contentFileName: d.content_file_name || null,
      changeSummary: d.change_summary || null,
    }));
  }, []);

  const loadRecords = useCallback(async (sid) => {
    if (!sid) return [];
    const { data } = await supabase
      .from('supplier_onboarding_documents')
      // review_decision (+ reason/notes) MUST be selected: without it a
      // rejected / needs-clarification upload has review_decision undefined and
      // the state machine falls through to "uploaded, not approved" =
      // awaiting_review — the exact desync where admin says Rejected and the
      // portal says "awaiting Fusion review". review_reason/notes back the
      // supplier-facing "From Fusion 2K" reason box.
      .select('document_key, expiry_date, expiry_approved_at, not_required, risk_override_applicable, supplier_uploaded_file_url, supplier_uploaded_file_name, supplier_uploaded_at, signed_at, sent_at, review_decision, review_reason, review_notes, reviewed_at, supplier_upload_note')
      .eq('supplier_id', sid)
      // Archived documents leave the supplier's working view entirely
      // (detail-page §5). The row and its history are kept; if the document is
      // still required, it simply reads as outstanding again.
      .is('archived_at', null);
    return data || [];
  }, []);

  // Acknowledge records — one per (supplier, document_key, document_version).
  // Loaded in parallel with supplier_onboarding_documents; the derived
  // memo below synthesises an upload-shaped record from these so the
  // existing computeDocumentState / groupDocumentsByState pipeline
  // doesn't need a separate code path for acknowledge-type docs.
  const loadAcknowledgements = useCallback(async (sid) => {
    if (!sid) return [];
    const { data } = await supabase
      .from('document_acknowledgements')
      // '*' so the supplier's read-only view can show the signature they gave
      // (signature_url). A named column that doesn't exist yet fails the whole
      // query in PostgREST, which would break compliance for every supplier in
      // the window between deploy and migration.
      .select('*')
      .eq('supplier_id', sid)
      .order('acknowledged_at', { ascending: false });
    return data || [];
  }, []);

  // Every PUBLISHED version of the supplier's required documents. Needed
  // because what a signature is worth is decided by the versions published
  // AFTER it (document-versioning brief A §1) — the live row alone only tells
  // us about the most recent publish.
  const loadVersionFamilies = useCallback(async (keys) => {
    if (!keys || keys.length === 0) return {};
    const { data, error } = await supabase
      .from('fusion_documents')
      .select('id, compliance_doc_key, version_value, status, resign_policy, resign_deadline, change_reason')
      .in('compliance_doc_key', keys)
      .neq('status', 'draft');
    // Pre-migration databases have no resign_policy column; the whole query
    // fails rather than returning partial rows, so treat it as "no version
    // history known" — nothing is owed, which is the pre-versioning behaviour.
    if (error) return {};
    const byKey = {};
    for (const row of data || []) {
      (byKey[row.compliance_doc_key] = byKey[row.compliance_doc_key] || []).push(row);
    }
    return byKey;
  }, []);

  // Which version did this supplier sign? Acknowledgements carry it directly;
  // hosted forms and pdf_sign record it on the submission.
  const loadSubmissions = useCallback(async (sid) => {
    if (!sid) return [];
    const { data } = await supabase
      .from('hosted_form_submissions')
      .select('document_key, document_version, signed_at')
      .eq('supplier_id', sid)
      .order('signed_at', { ascending: false });
    return data || [];
  }, []);

  // Reject/clarify thread — the full back-and-forth per document, oldest
  // first. Grouped by document_key for the DocumentRow that owns each doc.
  const loadClarificationMessages = useCallback(async (sid) => {
    if (!sid) return [];
    const { data } = await supabase
      .from('compliance_clarification_messages')
      .select('id, document_key, author, body, attachment_url, attachment_name, created_at')
      .eq('supplier_id', sid)
      .order('created_at', { ascending: true });
    return data || [];
  }, []);

  const loadRequiredKeys = useCallback(async (sid, flag) => {
    if (!sid) return [];
    // Required = risk_document_requirements at the supplier's tier
    // UNION per-supplier risk_override_applicable rows, MINUS rows
    // the supplier (or Fusion) has marked Not Required. Mirrors the
    // DB compute_supplier_compliance_status path.
    const [tierReq, overrides] = await Promise.all([
      flag
        ? supabase.from('risk_document_requirements').select('document_key').eq('risk_level', flag)
        : Promise.resolve({ data: [] }),
      supabase.from('supplier_onboarding_documents').select('document_key, risk_override_applicable, not_required').eq('supplier_id', sid),
    ]);
    const required = new Set((tierReq.data || []).map(r => r.document_key));
    for (const r of (overrides.data || [])) {
      if (r.risk_override_applicable) required.add(r.document_key);
    }
    for (const r of (overrides.data || [])) {
      if (r.not_required) required.delete(r.document_key);
    }
    return Array.from(required);
  }, []);

  // Fetch the supplier-level manual override. When non-null this takes
  // precedence over the doc-derived state — mirrors the DB rule that
  // recompute_supplier_compliance_status leaves compliance_status alone
  // while an override is in force, so the supplier portal stays in
  // lockstep with what admins see on SupplierDetail.
  const loadOverride = useCallback(async (sid) => {
    if (!sid) return null;
    const { data } = await supabase
      .from('suppliers')
      .select('compliance_status_override')
      .eq('id', sid)
      .single();
    return data?.compliance_status_override || null;
  }, []);

  const refetch = useCallback(async () => {
    if (!supplierId) {
      setLoading(false);
      return;
    }
    const [docs, recs, acks, msgs, keys, override, subs] = await Promise.all([
      loadMasterDocs(),
      loadRecords(supplierId),
      loadAcknowledgements(supplierId),
      loadClarificationMessages(supplierId),
      loadRequiredKeys(supplierId, riskFlag),
      loadOverride(supplierId),
      loadSubmissions(supplierId),
    ]);
    setRequiredDocs(docs);
    setRecords(recs);
    setAcknowledgements(acks);
    setClarificationMessages(msgs);
    setRequiredKeys(keys);
    setOverrideStatus(override);
    setSubmissions(subs);
    setVersionsByKey(await loadVersionFamilies(docs.map(d => d.key).filter(Boolean)));
    setLoading(false);
  }, [supplierId, riskFlag, loadMasterDocs, loadRecords, loadAcknowledgements, loadClarificationMessages, loadRequiredKeys, loadOverride, loadSubmissions, loadVersionFamilies]);

  useEffect(() => {
    setLoading(true);
    refetch();
  }, [refetch]);

  // Memoise the derived state so consumers (stripe, status card, grouped
  // rows) don't recompute on every parent render.
  //
  // When the admin has set a manual override, that wins over the doc-
  // derived state — same precedence as the DB trigger. Counts and groups
  // stay doc-derived so the /portal/compliance breakdown still reflects
  // the underlying document reality (which is the supplier's actual
  // working surface); only the headline state + stripe text follow the
  // override.
  //
  // Acknowledge-type docs synthesised into the same `records` array
  // before the state machine runs. The synthesis maps acknowledgement
  // lifecycle onto the existing upload-shaped columns so
  // computeDocumentState doesn't need a parallel code path:
  //
  //   no ack row                       → no synthetic record → OUTSTANDING
  //   acked, review_decision=null      → supplier_uploaded_at set,
  //                                      expiry_approved_at null → AWAITING_REVIEW
  //   acked, review_decision='approved'→ supplier_uploaded_at +
  //                                      expiry_approved_at + signed_at →
  //                                      APPROVED
  //   acked, review_decision='rejected'→ review_decision='rejected' on the
  //                                      synthetic record → OUTSTANDING
  //                                      (the supplier must re-acknowledge)
  //
  // For one-off acks (lifecycle='one_off') expiry_date stays null so
  // APPROVED never falls into EXPIRING_SOON / EXPIRED. Recurring-ack
  // expiry semantics will land alongside Phase 6 (version-driven re-ack).
  // Upload-type docs are passed through untouched so the existing
  // pipeline behaviour is preserved.
  const derived = useMemo(() => {
    const requiredKeySet = new Set(requiredKeys);
    const requiredMeta = requiredDocs.filter(d => requiredKeySet.has(d.key));

    // Build a lookup of (key → latest ack) — list is already sorted
    // acknowledged_at desc by the loader.
    const ackByKey = new Map();
    for (const a of acknowledgements) {
      if (!ackByKey.has(a.document_key)) ackByKey.set(a.document_key, a);
    }

    // Latest hosted-form / pdf_sign signature per key (loader sorts desc).
    const submissionByKey = new Map();
    for (const s of submissions) {
      if (!submissionByKey.has(s.document_key)) submissionByKey.set(s.document_key, s);
    }

    // What does this supplier still owe on a document they've already signed?
    // Decided by the versions published AFTER the one they signed
    // (document-versioning §1): nothing if every later publish said "nothing
    // changes"; a deadline under B; immediately under C. Returns null when
    // they've signed nothing, or when the document has no version history to
    // judge against (pre-migration databases).
    const standingFor = (key, signedVersion) => {
      if (signedVersion == null) return null;
      const family = versionsByKey[key];
      if (!family || family.length === 0) return null;
      const signedRow = family.find(v => Number(v.version_value) === Number(signedVersion));
      // The version they signed is gone from the family (deleted, or a legacy
      // signature with a number nothing matches) — don't invent a demand.
      if (!signedRow) return null;
      return signatureStandingFor(signedRow, family, { todayIso: todayIso() });
    };

    // Synthesise records. Walk the master meta so we know which docs
    // are acknowledge-type, then build an upload-shaped record from the
    // ack row (or leave the upload row alone for upload-type docs).
    const ackMetaByKey = new Map(requiredMeta.map(m => [m.key, m]));
    const synthesisedRecords = [];
    const seenKeys = new Set();
    for (const r of records) {
      const meta = ackMetaByKey.get(r.document_key);
      // Acknowledge-type doc that also has an old upload row (e.g. admin
      // converted an upload doc to acknowledge) — drop the stale upload
      // data so it doesn't drive the state machine alongside the ack.
      if (meta?.satisfiedBy === 'acknowledge') {
        seenKeys.add(r.document_key);
        continue;
      }
      seenKeys.add(r.document_key);
      // Hosted-form / pdf_sign documents flow through supplier_onboarding_
      // documents, so a re-sign demand has to be folded into that row.
      const sub = submissionByKey.get(r.document_key);
      const patch = resignRecordPatch(standingFor(r.document_key, sub?.document_version), r);
      if (!patch) { synthesisedRecords.push(r); continue; }
      // 'drop' = the signature no longer counts; no record → OUTSTANDING, and
      // the supplier is blocked from new work until they sign the new version.
      if (patch.drop) continue;
      synthesisedRecords.push({ ...r, expiry_date: patch.expiry_date, resign: patch.resign });
    }
    for (const meta of requiredMeta) {
      if (meta.satisfiedBy !== 'acknowledge') continue;
      const ack = ackByKey.get(meta.key);
      if (!ack) continue; // no ack → no record → OUTSTANDING
      // Version-driven re-acknowledgement. This USED to invalidate the ack on
      // ANY version mismatch — publishing a typo fix silently made every
      // supplier non-compliant. The publish now says what it wants
      // (document-versioning §1), so ask the version history instead: a later
      // "nothing changes" publish leaves the ack standing; a deadline leaves it
      // standing until that date; an immediate demand voids it now.
      //
      // Fallback when there's no version history to judge against (a
      // pre-versioning database): the old strict rule, so behaviour doesn't
      // silently loosen where the new metadata is missing.
      let resignInfo = null;
      let resignExpiry = null;
      const standing = standingFor(meta.key, ack.document_version);
      if (standing) {
        const patch = resignRecordPatch(standing, null);
        if (patch?.drop) continue; // must acknowledge the new version → OUTSTANDING
        resignInfo = patch?.resign || null;
        resignExpiry = patch?.expiry_date || null;
      } else {
        const masterVersion = meta.versionValue;
        if (masterVersion != null && ack.document_version != null && ack.document_version !== masterVersion) {
          continue; // version mismatch, nothing better to go on → OUTSTANDING
        }
      }
      const rejected = ack.review_decision === 'rejected';
      synthesisedRecords.push({
        document_key: meta.key,
        supplier_uploaded_at: ack.acknowledged_at,
        supplier_uploaded_file_url: null,
        supplier_uploaded_file_name: ack.signer_name || null,
        // 'approved' IS a valid review_decision on document_acknowledgements
        // (da_review_decision_check allows NULL/'approved'/'rejected') — unlike
        // supplier_onboarding_documents, whose CHECK allows only NULL/
        // 'rejected'/'needs_clarification'. Same column name, two vocabularies.
        // See isApprovedAcknowledgement in lib/complianceRecord.
        expiry_approved_at: isApprovedAcknowledgement(ack) ? (ack.reviewed_at || ack.acknowledged_at) : null,
        signed_at: isApprovedAcknowledgement(ack) ? (ack.reviewed_at || ack.acknowledged_at) : null,
        // A re-sign deadline behaves exactly like an expiry: compliant until
        // the date, then not. Reusing the field means counts, colours and the
        // blocking rule all work without a new document state.
        expiry_date: resignExpiry || null,
        not_required: false,
        risk_override_applicable: false,
        review_decision: rejected ? 'rejected' : null,
        review_notes: ack.review_notes || null,
        resign: resignInfo || null,
      });
    }

    // Count only requirement keys that have a LIVE master document. A risk-tier
    // requirement pointing at a draft/archived/removed doc is a phantom — it
    // must not inflate the supplier's total (e.g. "0/10" when only 3 documents
    // are actually set up on the Risk Flag page). requiredMeta = requiredKeys ∩
    // live docs, so its keys are the real, set-up requirements.
    const liveRequiredKeys = requiredMeta.map(m => m.key);
    const counts = computeComplianceCounts(liveRequiredKeys, synthesisedRecords);
    const derivedState = computeSupplierState(liveRequiredKeys, synthesisedRecords);
    const state = overrideStatus || derivedState;
    const groups = groupDocumentsByState(requiredMeta, synthesisedRecords);
    const stripe = stripeContent(state, counts, { overridden: Boolean(overrideStatus) });

    // Documents this supplier has signed where a NEWER version now needs
    // signing. Surfaced separately because the document rows alone can't say it
    // loudly enough — a re-sign deadline sits inside the ordinary
    // approved/expiring treatment, and the supplier needs to know before they
    // go looking. Includes lapsed ones (whose record was dropped, so they read
    // as Outstanding) so the dashboard can still explain WHY.
    const resignRequired = [];
    for (const meta of requiredMeta) {
      const signedVersion = ackByKey.get(meta.key)?.document_version
        ?? submissionByKey.get(meta.key)?.document_version
        ?? null;
      const standing = standingFor(meta.key, signedVersion);
      if (!standing || standing.state === 'valid') continue;
      resignRequired.push({
        key: meta.key,
        label: meta.label,
        state: standing.state,
        dueDate: standing.dueDate,
        reason: standing.reason,
      });
    }

    return { state, counts, stripe, requiredMeta, groups, resignRequired };
  }, [requiredKeys, requiredDocs, records, acknowledgements, overrideStatus, submissions, versionsByKey]);

  // Group the reject/clarify thread by document_key for the doc rows.
  const messagesByKey = useMemo(() => {
    const m = {};
    for (const msg of clarificationMessages) {
      (m[msg.document_key] = m[msg.document_key] || []).push(msg);
    }
    return m;
  }, [clarificationMessages]);

  return {
    loading,
    messagesByKey,                    // { document_key: [thread messages, oldest first] }
    state: derived.state,             // 'green' | 'amber' | 'red' — override wins over doc-derived
    counts: derived.counts,           // { approved, awaitingReview, expiringSoon, outstanding, expired, notRequired, total }
    stripe: derived.stripe,           // { tone, message, cta }
    requiredDocs: derived.requiredMeta, // master meta filtered to the supplier's required set
    groups: derived.groups,           // Map<state, [{ meta, record, state }]>
    // [{ key, label, state: 'resign_due'|'lapsed'|'invalid_now', dueDate, reason }]
    resignRequired: derived.resignRequired,
    records,                          // raw supplier_onboarding_documents rows
    acknowledgements,                 // raw document_acknowledgements rows (most recent first)
    overridden: Boolean(overrideStatus), // true when admin has manually flagged the supplier
    refetch,                          // call after an upload to refresh
    // Convenience flag — supplier portal renders the briefs blocked empty
    // state when this is true (red), or fully when false (green/amber).
    isBlocked: derived.state === SUPPLIER_STATES.RED,
  };
}
